
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import { useAuth } from "@/context/AuthContext";

const API_BASE = "https://guardiancloudcare.com/backend/api";
const EVIDENCE_BASE = "https://guardiancloudcare.com/backend/uploads/victim_reports";

interface SocialWorker {
  uid: string;
  name: string;
  email: string;
  office_name: string;
  service_municipality: string | null;
  service_province: string | null;
}

interface Report {
  [key: string]: any;
  report_uid: string;
  incident_type: string;
  urgency: string;
  description: string;
  location: string;
  address: string;
  barangay: string;
  municipality: string;
  province: string;
  status: string;
  created_at: string;
  assigned_social_worker_uid: string | null;
  assigned_social_worker_name: string | null;
  photo_evidence?: string | string[] | null;
  photo_evidence_list?: string[];
  video_evidence?: string | null;
}

function display(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function confidence(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) return String(value);

  return number >= 0 && number <= 1
    ? `${(number * 100).toFixed(1)}%`
    : `${number}%`;
}

function getPhotos(report: Report): string[] {
  if (Array.isArray(report.photo_evidence_list)) {
    return report.photo_evidence_list;
  }

  const raw = report.photo_evidence;

  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((item) => typeof item === "string");

  try {
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed)) {
      return parsed.filter((item) => typeof item === "string");
    }
  } catch {
    // Existing records may contain one filename instead of JSON.
  }

  return [raw];
}

function evidenceUrl(filename: string): string {
  return `${EVIDENCE_BASE}/${encodeURIComponent(filename)}`;
}

function Info({
  label,
  value,
}: {
  label: string;
  value: unknown;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
        {display(value)}
      </p>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-5 py-4">
        <h2 className="font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
        {children}
      </div>
    </section>
  );
}

export default function SupVictimReportDetailPage() {
  const { reportUid } = useParams<{ reportUid: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [workers, setWorkers] = useState<SocialWorker[]>([]);
  const [selectedWorkerUid, setSelectedWorkerUid] = useState("");
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [assignmentError, setAssignmentError] = useState("");

  const [showAssignModal, setShowAssignModal] = useState(false);

  const loadReport = useCallback(async () => {
    if (!user?.uid || !reportUid) {
      setError("Missing Supervisor account or report reference.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE}/get_supervisor_victim_report_detail.php` +
          `?supervisor_uid=${encodeURIComponent(user.uid)}` +
          `&report_uid=${encodeURIComponent(reportUid)}`
      );

      const result = await response.json();

      if (!response.ok || !result.success || !result.report) {
        throw new Error(result.message || "Unable to load report details.");
      }

      setReport(result.report);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to load report details."
      );
    } finally {
      setLoading(false);
    }
  }, [user?.uid, reportUid]);

  const loadAvailableWorkers = useCallback(async () => {
    if (!user?.uid || !reportUid) return;

    setLoadingWorkers(true);
    setAssignmentError("");

    try {
      const response = await fetch(
        `${API_BASE}/get_supervisor_available_workers.php` +
          `?supervisor_uid=${encodeURIComponent(user.uid)}` +
          `&report_uid=${encodeURIComponent(reportUid)}`
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to load Social Workers.");
      }

      const availableWorkers: SocialWorker[] = result.data?.workers ?? [];
      setWorkers(availableWorkers);

      setSelectedWorkerUid((current) =>
        availableWorkers.some((worker) => worker.uid === current)
          ? current
          : ""
      );
    } catch (err) {
      setAssignmentError(
        err instanceof Error ? err.message : "Unable to load Social Workers."
      );
    } finally {
      setLoadingWorkers(false);
    }
  }, [user?.uid, reportUid]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  useEffect(() => {
    void loadAvailableWorkers();
  }, [loadAvailableWorkers]);

  
  const assignSocialWorker = async () => {
    if (!user?.uid || !reportUid || !selectedWorkerUid) {
      setAssignmentError("Please select a Social Worker.");
      return;
    }

    const selectedWorker = workers.find(
      (worker) => worker.uid === selectedWorkerUid
    );

    if (!selectedWorker) {
      setAssignmentError("Please select a valid available Social Worker.");
      return;
    }

    setAssigning(true);
    setAssignmentError("");
    setAssignmentMessage("");

    try {
      const response = await fetch(
        `${API_BASE}/assign_social_worker_to_report.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            supervisor_uid: user.uid,
            report_uid: reportUid,
            social_worker_uid: selectedWorkerUid,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Assignment failed.");
      }

      setShowAssignModal(false);
      setAssignmentMessage(
        `Successfully assigned ${result.data?.social_worker_name ?? selectedWorker.name}.`
      );

      await loadReport();
      await loadAvailableWorkers();
    } catch (err) {
      setAssignmentError(
        err instanceof Error ? err.message : "Unable to assign Social Worker."
      );
    } finally {
      setAssigning(false);
    }
  };


  if (loading) {
    return (
      <AppLayout
        title="Report Details"
        breadcrumb={["Supervisor", "Victim Reports", "Details"]}
      >
        <div className="rounded-xl border bg-white p-10 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-teal-600" />
          <p className="mt-3 text-sm text-gray-500">Loading report details...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !report) {
    return (
      <AppLayout
        title="Report Details"
        breadcrumb={["Supervisor", "Victim Reports", "Details"]}
      >
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => navigate("/sup-victim-reports")}
            className="text-sm text-teal-700 hover:underline"
          >
            <i className="ri-arrow-left-line mr-1" />
            Back to Victim Reports
          </button>

          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-700">
            <p className="font-semibold">Unable to load report</p>
            <p className="mt-1 text-sm">{error || "Report not found."}</p>
            <button
              type="button"
              onClick={() => void loadReport()}
              className="mt-4 rounded-lg bg-red-700 px-4 py-2 text-sm text-white"
            >
              Retry
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const photos = getPhotos(report);
  const guardian = report.guardian ?? {};
  const parents = report.parents ?? {};
  const school = report.school ?? {};
  const medical = report.medical ?? {};
  const childName =
    report.child_full_name ||
    [
      report.child_first_name,
      report.child_middle_name,
      report.child_last_name,
    ]
      .filter(Boolean)
      .join(" ");

  const isAnonymous = Number(report.is_anonymous) === 1;

  return (
    <AppLayout
      title="Report Details"
      breadcrumb={["Supervisor", "Victim Reports", report.report_uid]}
    >
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => navigate("/sup-victim-reports")}
          className="text-sm font-medium text-teal-700 hover:underline"
        >
          <i className="ri-arrow-left-line mr-1" />
          Back to Victim Reports
        </button>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">
                Victim Report
              </p>
              <h1 className="mt-1 break-words text-2xl font-bold text-gray-900">
                {report.report_uid}
              </h1>
              {report.case_number && (
                <p className="mt-1 text-sm text-gray-500">
                  Case Number: {report.case_number}
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Submitted: {display(report.created_at)}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-orange-100 px-3 py-1.5 text-xs text-orange-700">
                {display(report.urgency)}
              </span>
              <span className="rounded-full bg-blue-100 px-3 py-1.5 text-xs text-blue-700">
                {display(report.status)}
              </span>
              <span className="rounded-full bg-purple-100 px-3 py-1.5 text-xs text-purple-700">
                Routing: {display(report.routing_status)}
              </span>
            </div>
          </div>
        </div>

        <Section title="Incident Information">
          <Info label="Incident Type" value={report.incident_type} />
          <Info label="Urgency" value={report.urgency} />
          <Info label="Location" value={report.location} />
          <Info label="Address" value={report.address} />
          <Info label="Barangay" value={report.barangay} />
          <Info label="Municipality" value={report.municipality} />
          <Info label="Province" value={report.province} />
          <Info label="Report Status" value={report.status} />
          <div className="md:col-span-2">
            <Info label="Incident Description" value={report.description} />
          </div>
        </Section>

        <Section title="Child Information">
          <Info label="Full Name" value={childName} />
          <Info label="Nickname" value={report.child_nickname} />
          <Info label="Birth Date" value={report.birth_date} />
          <Info label="Age" value={report.age} />
          <Info label="Gender" value={report.gender} />
          <Info label="Nationality" value={report.nationality} />
          <Info label="Religion" value={report.religion} />
          <Info label="Place of Birth" value={report.place_of_birth} />
          <div className="md:col-span-2">
            <Info label="Additional Information" value={report.child_info} />
          </div>
        </Section>

        <Section title="School Information">
          <Info label="School" value={school.school_name ?? report.school_name} />
          <Info label="Grade Level" value={school.grade_level ?? report.grade_level} />
          <Info label="Section" value={school.section_name ?? report.section_name} />
        </Section>

        <Section title="Family Information">
          <Info label="Father's Name" value={parents.father ?? report.father_name} />
          <Info label="Mother's Name" value={parents.mother ?? report.mother_name} />
        </Section>

        <Section title="Guardian Information">
          <Info label="Guardian Name" value={guardian.name ?? report.guardian_name} />
          <Info
            label="Relationship"
            value={guardian.relationship ?? report.guardian_relationship}
          />
          <Info label="Contact Number" value={guardian.contact ?? report.guardian_contact} />
          <Info label="Guardian Address" value={guardian.address ?? report.guardian_address} />
        </Section>

        <Section title="Medical Information">
          <Info
            label="Medical Conditions"
            value={medical.medical_conditions ?? report.medical_conditions}
          />
          <Info label="Medications" value={medical.medications ?? report.medications} />
          <Info
            label="With Disability"
            value={medical.with_disability ?? report.with_disability}
          />
        </Section>

        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="font-semibold text-gray-800">Photo and Video Evidence</h2>
          </div>
          <div className="space-y-6 p-5">
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                Photo Evidence ({photos.length})
              </h3>
              {photos.length ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {photos.map((filename, index) => (
                    <div
                      key={`${filename}-${index}`}
                      className="overflow-hidden rounded-xl border border-gray-200"
                    >
                      <a
                        href={evidenceUrl(filename)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={evidenceUrl(filename)}
                          alt={`Photo evidence ${index + 1}`}
                          loading="lazy"
                          className="h-56 w-full bg-gray-50 object-contain"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </a>
                      <p className="break-all border-t px-3 py-2 text-xs text-gray-600">
                        {filename}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No photo evidence attached.</p>
              )}
            </div>

            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                Video Evidence
              </h3>
              {report.video_evidence ? (
                <video
                  key={report.video_evidence}
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full max-w-3xl rounded-xl border bg-black"
                >
                  <source src={evidenceUrl(report.video_evidence)} />
                  Your browser does not support video playback.
                </video>
              ) : (
                <p className="text-sm text-gray-500">No video evidence attached.</p>
              )}
            </div>
          </div>
        </section>

        <Section title="AI Classification Results">
          <Info label="Suggested Category" value={report.ai_suggested_category} />
          <Info
            label="Category Confidence"
            value={confidence(report.ai_category_confidence)}
          />
          <Info label="Suggested Urgency" value={report.ai_suggested_urgency} />
          <Info
            label="Urgency Confidence"
            value={confidence(report.ai_urgency_confidence)}
          />
          <Info
            label="Requires Validation"
            value={
              report.ai_requires_validation === undefined
                ? "Not provided"
                : Number(report.ai_requires_validation) === 1
                  ? "Yes"
                  : "No"
            }
          />
          <div className="md:col-span-2">
            <Info label="AI Reason" value={report.ai_reason} />
          </div>
        </Section>

        <Section title="Reporter Information">
          <Info
            label="Anonymous Report"
            value={isAnonymous ? "Yes" : "No"}
          />
          <Info
            label="Reporter Name"
            value={isAnonymous ? "Anonymous" : report.reporter_name}
          />
          <Info
            label="Reporter Contact"
            value={
              isAnonymous
                ? "Hidden for anonymous report"
                : report.reporter_contact
            }
          />
          <Info label="Victim UID" value={report.victim_uid} />
        </Section>

        {/* CASE ASSIGNMENT */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="font-semibold text-gray-800">Case Assignment</h2>
            <p className="mt-1 text-sm text-gray-500">
              Choose an approved Social Worker from your office.
            </p>
          </div>

          <div className="space-y-5 p-5">
            {report.assigned_social_worker_name ||
            report.assigned_social_worker_uid ? (
              <div className="flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50 p-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-100">
                  <i className="ri-user-check-line text-xl text-teal-700" />
                </div>
                <div>
                  <p className="text-xs font-medium text-teal-700">
                    CURRENTLY ASSIGNED
                  </p>
                  <p className="font-semibold text-gray-800">
                    {display(report.assigned_social_worker_name)}
                  </p>
                  <p className="text-sm text-gray-500">
                    UID: {display(report.assigned_social_worker_uid)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                <p className="font-medium text-orange-800">
                  No Social Worker Assigned
                </p>
                <p className="mt-1 text-sm text-orange-700">
                  Select a Social Worker below to assign this case.
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="social-worker"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Available Social Workers
              </label>

              <select
                id="social-worker"
                value={selectedWorkerUid}
                onChange={(event) => {
                  setSelectedWorkerUid(event.target.value);
                  setAssignmentError("");
                  setAssignmentMessage("");
                }}
                disabled={
                  loadingWorkers || assigning || workers.length === 0
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              >
                <option value="">
                  {loadingWorkers
                    ? "Loading Social Workers..."
                    : workers.length === 0
                      ? "No available Social Workers"
                      : "Select a Social Worker"}
                </option>

                {workers.map((worker) => (
                  <option key={worker.uid} value={worker.uid}>
                    {worker.name} ({worker.uid})
                  </option>
                ))}
              </select>

              <p className="mt-2 text-xs text-gray-500">
                Only approved Social Workers from your office are listed.
              </p>
            </div>

            {assignmentError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {assignmentError}
              </div>
            )}

            {assignmentMessage && (
              <div
                role="status"
                className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700"
              >
                {assignmentMessage}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void loadAvailableWorkers()}
                disabled={loadingWorkers || assigning}
                className="rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <i className="ri-refresh-line mr-2" />
                Refresh Workers
              </button>

              <button
                type="button"
                onClick={() => {
                  setAssignmentError("");
                  setAssignmentMessage("");
                  setShowAssignModal(true);
                }}
                disabled={
                  assigning ||
                  loadingWorkers ||
                  !selectedWorkerUid ||
                  workers.length === 0
                }
                className="rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-user-add-line mr-2" />
                Assign Social Worker
              </button>
            </div>
          </div>
        </section>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <i className="ri-shield-check-line text-xl text-blue-600" />
            <div>
              <p className="font-semibold text-blue-800">
                Confidential Case Information
              </p>
              <p className="mt-1 text-sm leading-6 text-blue-700">
                This page contains sensitive child-protection information and
                evidence. Access must be limited to authorized personnel.
              </p>
            </div>
          </div>
        </div>
        
        {/* CUSTOM ASSIGNMENT CONFIRMATION MODAL */}
        {showAssignModal &&
          (() => {
            const selectedWorker = workers.find(
              (worker) => worker.uid === selectedWorkerUid
            );

            if (!selectedWorker) return null;

            return (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget && !assigning) {
                    setShowAssignModal(false);
                  }
                }}
              >
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="assignment-modal-title"
                  className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
                >
                  {/* Modal header */}
                  <div className="relative bg-gradient-to-r from-teal-700 to-teal-500 px-6 py-6 text-white">
                    <button
                      type="button"
                      aria-label="Close dialog"
                      disabled={assigning}
                      onClick={() => setShowAssignModal(false)}
                      className="absolute right-4 top-4 rounded-lg p-2 text-white/80 transition hover:bg-white/15 hover:text-white disabled:opacity-50"
                    >
                      <i className="ri-close-line text-xl" />
                    </button>

                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/15">
                      <i className="ri-user-shared-2-line text-3xl" />
                    </div>

                    <h2
                      id="assignment-modal-title"
                      className="mt-4 text-xl font-bold"
                    >
                      Confirm Case Assignment
                    </h2>

                    <p className="mt-1 text-sm text-teal-50">
                      Review the details before assigning this case.
                    </p>
                  </div>

                  {/* Modal body */}
                  <div className="space-y-4 p-6">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Report Reference
                      </p>
                      <p className="mt-1 break-all font-semibold text-gray-900">
                        {reportUid}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Case No.: {display(report?.case_number)}
                      </p>
                    </div>

                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Assigning to
                      </p>

                      <div className="flex items-center gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                          <i className="ri-user-3-line text-2xl" />
                        </div>

                        <div className="min-w-0">
                          <p className="break-words font-semibold text-gray-900">
                            {selectedWorker.name}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">
                            UID: {selectedWorker.uid}
                          </p>
                          <p className="mt-1 break-words text-xs text-teal-700">
                            {selectedWorker.office_name}
                          </p>
                        </div>

                        <i className="ri-checkbox-circle-fill ml-auto shrink-0 text-xl text-teal-600" />
                      </div>
                    </div>

                    {assignmentError && (
                      <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                      >
                        <i className="ri-error-warning-line mr-2" />
                        {assignmentError}
                      </div>
                    )}

                    <p className="text-sm leading-6 text-gray-500">
                      Do you want to assign this report to the selected Social Worker?
                      The assignment will be saved to the case record.
                    </p>

                    {/* Modal actions */}
                    <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        disabled={assigning}
                        onClick={() => setShowAssignModal(false)}
                        className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        disabled={assigning}
                        onClick={() => void assignSocialWorker()}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {assigning ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg" />
                            Assigning...
                          </>
                        ) : (
                          <>
                            <i className="ri-check-line text-lg" />
                            Confirm Assignment
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

      </div>
    </AppLayout>
  );
}
